import { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { closestIndexTo } from 'date-fns';
import { Box, Flex, VStack, Skeleton, Tabs, TabList, TabPanels, Tab, TabPanel, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import useCommonToast from '../../hooks/useCommonToast';

import { validateFields } from '../../services/items';

import EmptyGraph from '../itemData/EmptyGraph';

import DataGrid from '../grid/DataGrid';
import DataGridWrapper from '../grid/DataGridWrapper';

import ConfirmationModal from '../utils/ConfirmationModal';
import DataChart from '../utils/DataChart';
import Tooltip from '../utils/Tooltip';
import AddRowsModal from '../utils/AddRowsModal';
import SecondaryButton from '../utils/SecondaryButton';

import { ReactComponent as GraphIcon } from '../../icons/graph.svg';
import { ReactComponent as TableRowsIcon } from '../../icons/table-rows.svg';
import { ReactComponent as DeleteIcon } from '../../icons/delete.svg';
import { ReactComponent as AddIcon } from '../../icons/add-item.svg';

const SubItemData = ({
    hasChart,
    actionBarSlot,
    tab,
    yAxisLabel,
    chartResource,
    gridResource,
    columns,
    addRowsValidationSchema,
    formatNewRows,
    onLoadingStart,
    onLoadingEnd,
}) => {
    const toast = useCommonToast();
    const intl = useIntl();
    const gridApi = useRef();
    const deleteAllModal = useDisclosure();
    const addDataModal = useDisclosure();

    const [isChartLoading, setIsChartLoading] = useState(false);
    const [isAddingData, setIsAddingData] = useState(false);
    const [index, setIndex] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [isChartLoaded, setIsChartLoaded] = useState(false);

    const onChartRead = useCallback(
        async (range, existingData) => {
            const chartTabKey = `${tab}-0`;

            onLoadingStart(chartTabKey);
            setIsChartLoading(true);

            try {
                const pagination = {
                    pointCount: range ? 5000 : 2000,
                    startDateFrom: range ? range.start : null,
                    startDateTo: range ? range.end : null,
                };

                const data = await chartResource.read(pagination);

                if (range && existingData) {
                    // @todo can we instead use the same approach shown here?
                    // the `navigator.series` is different from the root `series`
                    // https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/stock/demo/lazy-loading/

                    // insert the new granular data between the 2 data points that are closest to the received data
                    const existingDates = existingData.map(([date]) => date);
                    const startIndex = closestIndexTo(data[0][0], existingDates);
                    const endIndex = closestIndexTo(data[data.length - 1][0], existingDates) + 1;

                    const result = existingData.slice(0, startIndex).concat(data, existingData.slice(endIndex));

                    setChartData(result);
                } else {
                    // setup/reset everything
                    setChartData(data);
                }
            } catch (err) {
                if (err?.response?.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_generic_loading_error' }),
                    });
                }
            } finally {
                setIsChartLoading(false);
                onLoadingEnd(chartTabKey);
            }
        },
        // Chakra's toast identity changes too often
        [chartResource, intl, tab, onLoadingStart, onLoadingEnd] // eslint-disable-line
    );

    const serverSideDatasource = useMemo(() => {
        return {
            async getRows(params) {
                const gridTabKey = `${tab}-1`;
                onLoadingStart(gridTabKey);

                const page = params.request.startRow / 500 + 1;
                const sortModel = params.request.sortModel[0] || null;
                const pagination = {
                    page,
                    sortColumn: sortModel ? sortModel.colId : null,
                    sortByDescending: sortModel?.sort === 'desc',
                };

                const data = await gridResource.read(pagination);

                if (data.items.length === 0) {
                    params.api.showNoRowsOverlay();
                } else {
                    params.api.hideOverlay();
                }

                params.success({ rowData: data.items, rowCount: data.totalCount });

                onLoadingEnd(gridTabKey);
            },
        };
    }, [gridResource, tab, onLoadingStart, onLoadingEnd]);

    const onGridReady = useCallback((params) => (gridApi.current = params.api), []);

    const onDeleteRow = useCallback(
        async (rowIds) => {
            const rowsForDeletion = rowIds.map((id) => gridApi.current.getRowNode(id).data);

            const gridTabKey = `${tab}-1`;
            onLoadingStart(gridTabKey);

            try {
                await gridResource.delete(rowsForDeletion);

                toast(intl.formatMessage({ id: 'common_delete_grid_msg' }, { n: rowsForDeletion.length }));

                gridApi.current.refreshServerSideStore({
                    purge: true,
                });
            } catch (err) {
                if (err?.response?.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_generic_delete_error' }),
                    });
                }
            } finally {
                onLoadingEnd(gridTabKey);
            }
        },
        // Chakra's toast identity changes too often
        [gridResource, intl, tab, onLoadingStart, onLoadingEnd] // eslint-disable-line
    );

    const onDataChange = useCallback(
        async (rowIds) => {
            const rows = rowIds.map((id) => gridApi.current.getRowNode(id).data);

            const gridTabKey = `${tab}-1`;
            onLoadingStart(gridTabKey);

            try {
                await gridResource.update(rows);

                toast(intl.formatMessage({ id: 'common_generic_item_change_success' }));
            } catch (err) {
                if (err?.response?.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                    });
                }
            } finally {
                onLoadingEnd(gridTabKey);
            }
        },
        // Chakra's toast identity changes too often
        [gridResource, intl, tab, onLoadingStart, onLoadingEnd] // eslint-disable-line
    );

    const onDeleteAllConfirm = useCallback(
        async () => {
            deleteAllModal.onClose();

            const gridTabKey = `${tab}-1`;
            onLoadingStart(gridTabKey);

            try {
                await gridResource.deleteAll();

                toast(intl.formatMessage({ id: 'common_delete_all_modal_on_success' }));

                gridApi.current.refreshServerSideStore({
                    purge: true,
                });
            } catch (error) {
                if (error?.response?.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_delete_all_modal_on_error' }),
                    });
                }
            } finally {
                onLoadingEnd(gridTabKey);
            }
        },
        // Chakra's toast and modal identity changes too often
        [gridResource, intl, tab, onLoadingStart, onLoadingEnd] // eslint-disable-line
    );

    const onCreateData = useCallback(
        async (rows) => {
            const gridTabKey = `${tab}-1`;
            onLoadingStart(gridTabKey);

            setIsAddingData(true);

            try {
                await gridResource.create(rows);

                toast(intl.formatMessage({ id: 'common_generic_item_change_success' }));

                gridApi.current.refreshServerSideStore({
                    purge: true,
                });

                setIsAddingData(false);
                addDataModal.onClose();
            } catch (err) {
                setIsAddingData(false);

                if (err?.response?.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                    });
                }
            } finally {
                onLoadingEnd(gridTabKey);
            }
        },
        // Chakra's toast identity changes too often
        [gridResource, intl, tab, onLoadingStart, onLoadingEnd] // eslint-disable-line
    );

    const onViewChange = useCallback((newIndex) => setIndex(newIndex), []);

    const getValidNewRowsBySchema = useCallback((rows, schema) => {
        return rows.map((row) => {
            const fields = Object.values(schema).map((v) => ({ ...v, value: row[v.name] }));

            return validateFields(fields);
        });
    }, []);

    const addDataGridColumns = useMemo(() => {
        return columns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
        }));
    }, [columns]);

    useEffect(() => {
        if (hasChart) {
            onChartRead().finally(() => {
                setIsChartLoaded(true);
            });
        }
    }, [hasChart, onChartRead]);

    const grid = (
        <DataGridWrapper>
            <DataGrid
                serverSideDatasource={serverSideDatasource}
                onGridReady={onGridReady}
                onDataChange={onDataChange}
                onDelete={onDeleteRow}
                rowModelType="serverSide"
                serverSideStoreType="partial"
                paginationPageSize={500}
                cacheBlockSize={500}
                columns={columns}
                isInitialRequestSent={true}
                noDataMessageId="common_no_data_found"
            />
        </DataGridWrapper>
    );

    return (
        <>
            {hasChart ? (
                <Tabs index={index} onChange={onViewChange} variant="icon" isLazy isManual lazyBehavior="keepMounted">
                    <Flex justifyContent="flex-end" wrap="wrap">
                        <Flex
                            order={{ base: 1, lg: 0 }}
                            w={{ base: '100%', lg: 'auto' }}
                            justifyContent="flex-end"
                            mt={{ base: 4, lg: 0 }}
                            mb={{ base: 4, lg: 0 }}
                        >
                            {actionBarSlot && actionBarSlot(index)}

                            {index === 1 && (
                                <>
                                    <SecondaryButton
                                        variant="secondary"
                                        mr={4}
                                        size="sm"
                                        leftIcon={<DeleteIcon />}
                                        onClick={deleteAllModal.onOpen}
                                        textTransform="capitalize"
                                    >
                                        <FormattedMessage id="common_delete_all_btn" />
                                    </SecondaryButton>

                                    <SecondaryButton
                                        variant="secondary"
                                        size="sm"
                                        leftIcon={<AddIcon />}
                                        onClick={addDataModal.onOpen}
                                        textTransform="capitalize"
                                    >
                                        <FormattedMessage id="common_add_rows_btn" />
                                    </SecondaryButton>
                                </>
                            )}
                        </Flex>

                        <TabList ml={4}>
                            <Tab>
                                <Tooltip label={intl.formatMessage({ id: 'common_graph_btn_aria_label' })}>
                                    <GraphIcon />
                                </Tooltip>
                            </Tab>

                            <Tab>
                                <Tooltip label={intl.formatMessage({ id: 'common_tabular_btn_aria_label' })}>
                                    <TableRowsIcon />
                                </Tooltip>
                            </Tab>
                        </TabList>
                    </Flex>

                    <TabPanels mt={2}>
                        <TabPanel>
                            {isChartLoaded ? (
                                chartData.length > 0 ? (
                                    <DataChart
                                        tooltipName="Load"
                                        yAxisLabel={yAxisLabel}
                                        getData={onChartRead}
                                        data={chartData}
                                        isLoading={isChartLoading}
                                    />
                                ) : (
                                    <EmptyGraph messageId="common_no_data_found" />
                                )
                            ) : (
                                <VStack spacing={4} align="stretch">
                                    <Skeleton h="56px" />
                                    <Skeleton h="56px" />
                                    <Skeleton h="56px" />
                                    <Skeleton h="56px" />
                                    <Skeleton h="56px" />
                                </VStack>
                            )}
                        </TabPanel>

                        <TabPanel>{grid}</TabPanel>
                    </TabPanels>
                </Tabs>
            ) : (
                <Box>
                    <Flex mb={6} justifyContent="flex-end">
                        <SecondaryButton
                            variant="secondary"
                            mr={4}
                            size="sm"
                            leftIcon={<DeleteIcon />}
                            onClick={deleteAllModal.onOpen}
                            textTransform="capitalize"
                        >
                            <FormattedMessage id="common_delete_all_btn" />
                        </SecondaryButton>

                        <SecondaryButton
                            variant="secondary"
                            size="sm"
                            leftIcon={<AddIcon />}
                            onClick={addDataModal.onOpen}
                            textTransform="capitalize"
                        >
                            <FormattedMessage id="common_add_rows_btn" />
                        </SecondaryButton>
                    </Flex>

                    {grid}
                </Box>
            )}

            {deleteAllModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={deleteAllModal.onClose}
                    header={<FormattedMessage id="common_delete_all_modal_heading" />}
                    hasExtraStep
                    content={<FormattedMessage id="common_delete_all_modal_description" />}
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_delete" />
                        </Box>
                    }
                    onConfirm={onDeleteAllConfirm}
                />
            )}

            {addDataModal.isOpen && (
                <AddRowsModal
                    isOpen
                    onClose={addDataModal.onClose}
                    columns={addDataGridColumns}
                    isLoading={isAddingData}
                    columnSchema={addRowsValidationSchema}
                    validate={getValidNewRowsBySchema}
                    format={formatNewRows}
                    addRows={onCreateData}
                    tabIndex={null} // not used
                />
            )}
        </>
    );
};

export default SubItemData;
